import { FC, useCallback, useMemo } from 'react';

import { Media } from 'shared/components/Carousel';
import { cardOfferDataSelector, useCardContext } from 'shared/context/CardContext';
import { memoWithDisplayName } from 'shared/utils/memoWithDisplayName';
import { noop } from 'shared/utils/noop';

import { TopLabelsContainer } from '../TopLabels';

import { CardGallery } from './CardGallery';
import { SlideOverlayContent } from './internal/components/SlideOverlayContent';

type GetIsDeoptimised = (media: Media, index: number) => boolean;

export const CardGalleryContainer: FC = memoWithDisplayName(() => {
  const cardData = useCardContext();

  const {
    media: { photos, moderationFirstPhotoOverlay },
  } = cardOfferDataSelector(cardData);

  const galleryMedia = useMemo<Media[]>(() => {
    const result = photos.map<Media>(photo => ({ id: photo.id.toString(), src: photo.src }));

    if (!result.length) {
      result.push({ id: 'empty_image', src: '' });
    }

    if (moderationFirstPhotoOverlay) {
      result[0].overlay = {
        background: moderationFirstPhotoOverlay.background,
        content: <SlideOverlayContent>{moderationFirstPhotoOverlay.text}</SlideOverlayContent>,
      };
    }

    return result;
  }, [moderationFirstPhotoOverlay, photos]);

  const getIsDeoptimised = useCallback<GetIsDeoptimised>(
    (_media, index) => index === 0 && cardData.index === 0,
    [cardData.index],
  );

  return (
    <CardGallery
      getIsDeoptimised={getIsDeoptimised}
      labels={<TopLabelsContainer />}
      media={galleryMedia}
      withControls={galleryMedia.length > 1}
      withCount={!!photos.length}
      onChangeSlide={noop}
    />
  );
}, 'CardGalleryContainer');
